import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, RefreshCw, AlertCircle, Sparkles, Crown } from 'lucide-react';
import { toast } from 'sonner';

/**
 * PaymentSuccess - Double-verification payment success page
 * 
 * SECURITY: This page NEVER grants access based on URL alone.
 * It performs a double-verification with the backend:
 * 1. First check: Quick status check
 * 2. Second check: Full verification with Stripe API if needed
 * 
 * Flow:
 * - User arrives with session_id from Stripe redirect
 * - Page calls /api/subscriptions/verify-payment/{session_id}
 * - Backend verifies with Stripe and activates subscription if webhook delayed
 * - User is redirected to dashboard only after confirmed activation
 */

const PLAN_NAMES = {
  essentiel: '🌿 Essentiel',
  experience: '👑 Vision Lab Experience'
};

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token, checkAuth } = useAuth();
  
  const [status, setStatus] = useState('verifying'); // verifying, success, pending, error
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);

  const sessionId = searchParams.get('session_id');
  
  const MAX_AUTO_RETRIES = 10;
  const RETRY_INTERVAL = 3000; // 3 seconds

  const verifyPayment = useCallback(async (isManualRetry = false) => {
    if (!sessionId || !token) {
      setStatus('error');
      setErrorMessage('Session de paiement invalide ou non authentifié');
      return;
    }

    if (isManualRetry) {
      setIsRetrying(true);
    }

    try {
      const response = await axios.get(
        `${API}/subscriptions/verify-payment/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const data = response.data;

      if (data.status === 'success' && data.subscription_active) {
        // Subscription is active!
        setStatus('success');
        setSubscriptionData(data);
        
        // Refresh auth context to update user state
        await checkAuth();
        
        toast.success('Bienvenue dans Vision Lab!', {
          description: 'Votre abonnement est maintenant actif.'
        });
      } else if (data.status === 'pending') {
        // Payment still processing
        setStatus('pending');
        
        if (!isManualRetry && verificationAttempts < MAX_AUTO_RETRIES) {
          // Schedule automatic retry
          setTimeout(() => {
            setVerificationAttempts(prev => prev + 1);
          }, RETRY_INTERVAL);
        }
      } else if (data.status === 'error') {
        setStatus('error');
        setErrorMessage(data.message || 'Une erreur est survenue');
      }
    } catch (error) {
      console.error('Verification error:', error);
      
      if (!isManualRetry && verificationAttempts < MAX_AUTO_RETRIES) {
        // Network error, retry
        setTimeout(() => {
          setVerificationAttempts(prev => prev + 1);
        }, RETRY_INTERVAL);
      } else {
        setStatus('error');
        setErrorMessage(error.response?.data?.detail || 'Impossible de vérifier le paiement');
      }
    } finally {
      setIsRetrying(false);
    }
  }, [sessionId, token, verificationAttempts, checkAuth]);

  // Initial verification and auto-retry loop
  useEffect(() => {
    verifyPayment();
  }, [verificationAttempts]); // Trigger on attempts change

  // Manual retry handler
  const handleManualRetry = () => {
    setVerificationAttempts(0);
    verifyPayment(true);
  };

  // Navigate to dashboard
  const goToDashboard = () => {
    navigate('/dashboard');
  };

  // No session_id provided
  if (!sessionId) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <div className="bg-gradient-to-b from-[#0A0A0A] to-[#111] border border-red-900/30 rounded-2xl p-12 text-center">
            <AlertCircle className="text-red-400 mx-auto mb-6" size={64} />
            <h2 className="font-serif text-2xl text-white mb-4">Session invalide</h2>
            <p className="text-[#A1A1AA] mb-8">
              Aucune session de paiement n&apos;a été trouvée. Veuillez recommencer le processus d&apos;abonnement.
            </p>
            <Button 
              onClick={() => navigate('/pricing')} 
              className="bg-[#D4AF37] hover:bg-[#E5C585] text-black px-8 py-4 font-medium"
              data-testid="go-pricing-btn"
            >
              Voir les abonnements
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <AnimatePresence mode="wait">
        {/* Verifying / Pending State */}
        {(status === 'verifying' || status === 'pending') && (
          <motion.div
            key="verifying"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="max-w-md w-full"
          >
            <div className="bg-gradient-to-b from-[#0A0A0A] to-[#111] border border-[#D4AF37]/20 rounded-2xl p-12 text-center">
              <div className="relative mx-auto w-20 h-20 mb-8">
                <Loader2 className="text-[#E5C585] w-full h-full animate-spin" />
                <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-[#D4AF37] w-8 h-8" />
              </div>
              
              <h2 className="font-serif text-2xl text-white mb-4">
                Vérification du paiement
              </h2>
              
              <p className="text-[#A1A1AA] mb-6">
                {status === 'verifying' 
                  ? 'Nous vérifions votre paiement auprès de Stripe...'
                  : 'Votre paiement est en cours de traitement. Veuillez patienter quelques instants...'
                }
              </p>
              
              {verificationAttempts > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-center gap-2 text-sm text-[#A1A1AA]">
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Tentative {Math.min(verificationAttempts + 1, MAX_AUTO_RETRIES)}/{MAX_AUTO_RETRIES}</span>
                  </div>
                  <div className="mt-3 h-1 bg-[#1C1C1C] rounded-full overflow-hidden">
                    <motion.div 
                      className="h-full bg-gradient-to-r from-[#D4AF37] to-[#E5C585]"
                      initial={{ width: 0 }}
                      animate={{ width: `${((verificationAttempts + 1) / MAX_AUTO_RETRIES) * 100}%` }}
                      transition={{ duration: 0.5 }}
                    />
                  </div>
                </div>
              )}
              
              {verificationAttempts >= MAX_AUTO_RETRIES && (
                <div className="space-y-4">
                  <p className="text-sm text-[#A1A1AA]">
                    La vérification prend plus de temps que prévu.
                  </p>
                  <Button 
                    onClick={handleManualRetry}
                    disabled={isRetrying}
                    className="bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-6 py-3"
                    data-testid="manual-retry-btn"
                  >
                    {isRetrying ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Vérification...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Réessayer la vérification
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Success State */}
        {status === 'success' && subscriptionData && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="max-w-lg w-full"
          >
            <div className="bg-gradient-to-b from-[#0A0A0A] to-[#111] border border-[#D4AF37]/30 rounded-2xl p-12 text-center relative overflow-hidden">
              {/* Background decoration */}
              <div className="absolute inset-0 opacity-50" style={{backgroundImage: "radial-gradient(circle at 25% 25%, rgba(212, 175, 55, 0.05) 0%, transparent 50%)"}} />
              
              <div className="relative z-10">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                >
                  <CheckCircle className="text-green-400 mx-auto mb-6" size={80} />
                </motion.div>
                
                <h2 className="font-serif text-3xl text-white mb-2">
                  Bienvenue dans Vision Lab!
                </h2>
                
                <p className="text-[#E5C585] font-medium mb-6">
                  Votre abonnement est maintenant actif
                </p>
                
                {/* Subscription details card */}
                <div className="bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-xl p-6 mb-8 text-left">
                  <div className="flex items-center gap-3 mb-4">
                    <Crown className="text-[#D4AF37]" size={24} />
                    <span className="text-white font-medium text-lg">
                      {PLAN_NAMES[subscriptionData.subscription_tier] || subscriptionData.subscription_tier}
                    </span>
                  </div>
                  
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">Facturation</span>
                      <span className="text-white">
                        {subscriptionData.subscription_billing_cycle === 'annual' ? 'Annuelle' : 'Mensuelle'}
                      </span>
                    </div>
                    
                    {subscriptionData.subscription_renewal_date && (
                      <div className="flex justify-between">
                        <span className="text-[#A1A1AA]">Prochain renouvellement</span>
                        <span className="text-white">
                          {new Date(subscriptionData.subscription_renewal_date).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    )}
                    
                    {subscriptionData.is_founding_visionary && (
                      <div className="flex justify-between items-center pt-2 border-t border-[#D4AF37]/20">
                        <span className="text-[#A1A1AA]">Statut</span>
                        <span className="text-[#D4AF37] flex items-center gap-1">
                          <Sparkles size={14} />
                          Founding Visionary (-20%)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                <p className="text-[#A1A1AA] mb-8">
                  Vous avez maintenant accès à tous les outils premium pour transformer votre marque.
                </p>
                
                <Button 
                  onClick={goToDashboard}
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#E5C585] hover:from-[#E5C585] hover:to-[#D4AF37] text-black px-8 py-5 font-semibold text-lg transition-all duration-300 shadow-lg shadow-[#D4AF37]/20"
                  data-testid="go-dashboard-success-btn"
                >
                  Accéder à mon Dashboard
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <motion.div
            key="error"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="max-w-md w-full"
          >
            <div className="bg-gradient-to-b from-[#0A0A0A] to-[#111] border border-red-900/30 rounded-2xl p-12 text-center">
              <AlertCircle className="text-red-400 mx-auto mb-6" size={64} />
              
              <h2 className="font-serif text-2xl text-white mb-4">
                Problème de vérification
              </h2>
              
              <p className="text-[#A1A1AA] mb-2">
                {errorMessage || 'Une erreur est survenue lors de la vérification de votre paiement.'}
              </p>
              
              <p className="text-sm text-[#71717A] mb-8">
                Si vous avez été débité, votre abonnement sera activé automatiquement sous quelques minutes.
                Vous pouvez également nous contacter à support@clierxter.com
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={handleManualRetry}
                  disabled={isRetrying}
                  className="bg-transparent border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10 px-6 py-4"
                  data-testid="error-retry-btn"
                >
                  {isRetrying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Vérification...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Réessayer
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={goToDashboard}
                  className="bg-[#D4AF37] hover:bg-[#E5C585] text-black px-6 py-4"
                  data-testid="go-dashboard-error-btn"
                >
                  Aller au Dashboard
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PaymentSuccess;
